import React, { useState, useEffect } from 'react';
import { signalRService, ProgressUpdate } from '../services/signalrService';

interface ProgressIndicatorProps {
  correlationId?: string;
  onComplete?: () => void;
  className?: string;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ 
  correlationId, 
  onComplete, 
  className = '' 
}) => {
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('');
  const [message, setMessage] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!correlationId) return;

    const handleProgressUpdate = (update: ProgressUpdate) => {
      // Only handle updates for our correlation ID
      if (update.correlationId === correlationId) {
        setProgress(update.progress);
        setStage(update.stage);
        setMessage(update.message);
        setIsVisible(true);

        // Hide progress indicator when complete
        if (update.progress >= 100) {
          setTimeout(() => {
            setIsVisible(false);
            onComplete?.();
          }, 2000);
        }
      }
    };

    // Register for progress updates
    signalRService.registerProgressCallback(`progress-${correlationId}`, handleProgressUpdate);

    return () => {
      // Cleanup
      signalRService.unregisterProgressCallback(`progress-${correlationId}`);
    };
  }, [correlationId, onComplete]);

  if (!isVisible) return null;

  return (
    <div className={`fixed top-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-80 z-50 ${className}`}>
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          {progress < 100 ? (
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          ) : (
            <div className="rounded-full h-6 w-6 bg-green-500 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium text-gray-900 truncate">
              {message}
            </p>
            <span className="text-sm text-gray-500">{progress}%</span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          
          {stage && (
            <p className="text-xs text-gray-500 mt-1 capitalize">
              {stage.replace(/_/g, ' ')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgressIndicator;